import { CSSProperties, useRef, useEffect, useState, useCallback } from 'react';
import { SectionData } from '../types';
import FloatingCharacter from './FloatingCharacter';
import './ParallaxSection.css';

interface ParallaxSectionProps {
  section: SectionData;
  isActive: boolean;
}

export default function ParallaxSection({ section, isActive }: ParallaxSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX,
        y: e.clientY - rect.top + window.scrollY
      });
    }
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (section) {
      section.addEventListener('mousemove', handleMouseMove);
      return () => section.removeEventListener('mousemove', handleMouseMove);
    }
  }, [handleMouseMove]);

  const sectionStyle = {
    '--section-fade': isActive ? 1 : 0.5,
    '--content-lift': isActive ? section.activeOffsetY ?? '0px' : '26px',
    '--background-shift': isActive ? '0px' : '18px',
    '--overlay-strength': isActive ? 0.58 : 0.82
  } as CSSProperties;

  return (
    <section
      className={`parallax-section panel ${isActive ? 'is-active' : 'is-inactive'}`}
      id={section.id}
      ref={sectionRef}
      style={sectionStyle}
      aria-hidden={!isActive}
    >
      <div
        className="parallax-background"
        style={{ backgroundImage: `url(${section.backgroundImage})` }}
      />
      
      {section.floatingCharacters.map((character, index) => (
        <FloatingCharacter
          key={index}
          {...character}
          mousePosition={mousePosition}
        />
      ))}

      <div className="content panel-scroll-region">
        {section.hasTextBackground ? (
          <div className="text-background">
            {section.eyebrow ? <span className="section-eyebrow">{section.eyebrow}</span> : null}
            <h1>{section.title}</h1>
            {section.description ? <p>{section.description}</p> : null}
            {section.highlights?.length ? (
              <div className="section-highlights">
                {section.highlights.map((highlight) => (
                  <div key={highlight} className="section-highlight-card">
                    {highlight}
                  </div>
                ))}
              </div>
            ) : null}
            {section.ctaLabel && section.ctaHref ? (
              <a
                className="section-cta"
                href={section.ctaHref}
                target={section.ctaExternal ? '_blank' : undefined}
                rel={section.ctaExternal ? 'noreferrer' : undefined}
              >
                {section.ctaLabel}
              </a>
            ) : null}
          </div>
        ) : (
          <>
            {section.eyebrow ? <span className="section-eyebrow">{section.eyebrow}</span> : null}
            <h1>{section.title}</h1>
            {section.description ? <p>{section.description}</p> : null}
            {section.highlights?.length ? (
              <div className="section-highlights">
                {section.highlights.map((highlight) => (
                  <div key={highlight} className="section-highlight-card">
                    {highlight}
                  </div>
                ))}
              </div>
            ) : null}
            {section.ctaLabel && section.ctaHref ? (
              <a
                className="section-cta"
                href={section.ctaHref}
                target={section.ctaExternal ? '_blank' : undefined}
                rel={section.ctaExternal ? 'noreferrer' : undefined}
              >
                {section.ctaLabel}
              </a>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
